import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json } from '../client.ts'
import { qk } from '../keys.ts'

/** One quiz given to one group: the group's own student link is `/l/<slug>`. */
export type Assignment = { id: string; group: string; slug: string; createdAt: string }
export type AssignmentSummary = Assignment & { started: number; handedIn: number }

/** One line of the Results page: a quiz and the group it went to (null: the general link). */
export type HubRow = {
	activityId: string
	title: string
	assignmentId: string | null
	group: string | null
	givenAt: string
	started: number
	handedIn: number
	/** Handed-in answers nobody has judged yet (the AI could not check them): hers to mark. */
	toCheck: number
	/** 0..100, null until something is marked. */
	average: number | null
	lastAt: string | null
}

/** The groups this quiz was already given to, oldest first. */
export const useAssignments = (activityId: string, enabled: boolean) =>
	useQuery({
		queryKey: qk.assignments(activityId),
		enabled,
		queryFn: async () =>
			(
				await json<{ assignments: AssignmentSummary[] }>(
					await api.activities[':id'].assignments.$get({ param: { id: activityId } }),
				)
			).assignments,
	})

/** Her group names, most recent first: what the group box suggests. */
export const useGroups = (enabled: boolean) =>
	useQuery({
		queryKey: qk.groups,
		enabled,
		queryFn: async () => (await json<{ groups: string[] }>(await api.groups.$get())).groups,
	})

/** The same group again answers with the link it already has, so asking twice is safe. */
export const useGiveToGroup = (activityId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (group: string) =>
			(
				await json<{ assignment: Assignment }>(
					await api.activities[':id'].assignments.$post({
						param: { id: activityId },
						json: { group },
					}),
				)
			).assignment,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: qk.assignments(activityId) })
			qc.invalidateQueries({ queryKey: qk.groups })
			qc.invalidateQueries({ queryKey: qk.resultsHub })
			qc.invalidateQueries({ queryKey: qk.results(activityId) })
		},
	})
}

/** Every quiz she gave, one row per group, newest first. */
export const useResultsHub = ({ enabled = true }: { enabled?: boolean } = {}) =>
	useQuery({
		queryKey: qk.resultsHub,
		enabled,
		queryFn: async () => (await json<{ rows: HubRow[] }>(await api.results.$get())).rows,
	})
